"use client";

import React, { use, useMemo } from "react";

import { type RouterOutputs } from "@revana/api";
import {
  type ChartConfig,
  Skeleton,
  ChartContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from "@revana/ui";
import { cn } from "@revana/ui/libs/utils";
import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
} from "date-fns";
import { Area, AreaChart, CartesianGrid } from "recharts";

import { useDateRangeQuery } from "@web/hooks";
import { api } from "@web/trpc/client";

const chartConfig = {
  value: {
    label: "Value",
    color: "hsl(var(--primary))",
  },
  grid: {
    color: "hsl(var(--border))",
  },
  label: {
    color: "hsl(var(--muted-foreground))",
  },
} satisfies ChartConfig;

interface CheckoutFunnelProps {
  initialData: Promise<RouterOutputs["store"]["getDashboardAnalytics"]>;
}

const CheckoutFunnel = ({ initialData }: CheckoutFunnelProps) => {
  const { dateRange, isEqual } = useDateRangeQuery();

  const initialAnalytics = use(initialData);

  const {
    data: analytics,
    isFetching,
    isFetchedAfterMount,
  } = api.store.getDashboardAnalytics.useQuery(dateRange, {
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    placeholderData: (data) => (!!data ? data : initialAnalytics),
  });

  const chartData = useMemo(() => {
    if (!analytics) {
      return [];
    }
    return [
      {
        label: "Abandoned Checkouts",
        value: analytics.abandonedCheckouts.currentPeriod.toFixed(0),
      },
      {
        label: "Abandoned Checkouts",
        value: analytics.abandonedCheckouts.currentPeriod.toFixed(0),
      },
      {
        label: "Outreach Attempts",
        value: analytics.outreachAttempts.currentPeriod.toFixed(0),
      },
      {
        label: "Outreach Responses",
        value: analytics.successfulOutreaches.currentPeriod.toFixed(0),
      },
      {
        label: "Checkouts Recovered",
        value: analytics.checkoutsRecovered.currentPeriod.toFixed(0),
      },
    ];
  }, [analytics]);

  const isRefetching = useMemo(() => {
    return (
      (isFetching && isFetchedAfterMount) ||
      (isFetching && !isEqual(dateRange, initialAnalytics.metadata))
    );
  }, [
    dateRange,
    initialAnalytics.metadata,
    isEqual,
    isFetchedAfterMount,
    isFetching,
  ]);

  const isNoData = useMemo(() => {
    return chartData.reduce((acc, curr) => acc + Number(curr.value), 0) === 0;
  }, [chartData]);

  const viewableDateRange = useMemo(() => {
    const daysInRange = differenceInDays(dateRange.to, dateRange.from);
    const weeksInRange = differenceInWeeks(dateRange.to, dateRange.from);
    const monthsInRange = differenceInMonths(dateRange.to, dateRange.from);
    const yearsInRange = differenceInYears(dateRange.to, dateRange.from);

    if (yearsInRange > 0) {
      return `Last ${yearsInRange} ${yearsInRange === 1 ? "Year" : "Years"}`;
    }
    if (monthsInRange > 0) {
      return `Last ${monthsInRange} ${monthsInRange === 1 ? "Month" : "Months"}`;
    }
    if (weeksInRange > 0) {
      return `Last ${weeksInRange} ${weeksInRange === 1 ? "Week" : "Weeks"}`;
    }
    return `Last ${daysInRange} ${daysInRange === 1 ? "Day" : "Days"}`;
  }, [dateRange]);

  return (
    <Card className="relative">
      {isRefetching && (
        <div className="absolute right-4 top-4">
          <Spinner width="24" />
        </div>
      )}
      <CardHeader>
        <CardTitle className="flex w-full items-center justify-between gap-2">
          Checkout Funnel
          <span className="rounded-md border border-border px-4 py-2 text-sm font-normal">
            {viewableDateRange}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="max-h-96 overflow-hidden p-0">
        {isNoData ? (
          <div className="py-4 text-center text-sm text-muted-foreground">
            No Checkout Data Available
          </div>
        ) : (
          <>
            <div
              className={cn(`grid grid-cols-${chartData.length - 1}`, "gap-4")}
            >
              {chartData.map((data, i) => {
                if (i === 0) {
                  return;
                }

                return (
                  <div
                    className="flex flex-col items-start pl-6"
                    key={data.label}
                  >
                    <div className="text-sm font-bold text-muted-foreground">
                      {data.label}
                    </div>
                    <div className="mt-1 text-3xl font-bold text-primary">
                      {data.value}
                    </div>
                  </div>
                );
              })}
            </div>
            <ChartContainer
              chartContainerProps={{ height: 82 * 4, width: "100%" }}
              className="z-10"
              config={chartConfig}
            >
              <AreaChart
                accessibilityLayer
                data={chartData}
                innerRadius={24}
                margin={{
                  left: 0,
                  right: 0,
                  top: 0,
                  bottom: 0,
                }}
                outerRadius={24}
              >
                <CartesianGrid horizontal={false} stroke="var(--color-grid)" />
                <defs>
                  <linearGradient id="fillValue" x1="0" x2="0" y1="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor="var(--color-value)"
                      stopOpacity={0.5}
                    />
                    <stop
                      offset="95%"
                      stopColor="var(--color-value)"
                      stopOpacity={0.1}
                    />
                  </linearGradient>
                </defs>
                <Area
                  animationDuration={750}
                  dataKey="value"
                  fill="url(#fillValue)"
                  stackId="a"
                  stroke="var(--color-value)"
                  type="linear"
                />
              </AreaChart>
            </ChartContainer>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const CheckoutFunnelFallback = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex w-full items-center justify-between gap-2">
          Checkout Funnel
          <span className="flex gap-2">
            <div className="flex gap-2 font-medium leading-none">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-4 rounded-full" />
            </div>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="h-96">
        <Skeleton className="h-full w-full" />
      </CardContent>
    </Card>
  );
};

export { CheckoutFunnelFallback };

export default CheckoutFunnel;
